import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, FormInstance, message } from 'antd';
import CartStep from 'enums/cart-step';
import useFarmOrderHandler from 'hooks/useFarmOrderHandler';
import {
  CartActionType,
  FarmProduct,
  useCartDispatch,
  useCartState,
  getCartStorageKey,
} from 'public/context/CartContext';
import { Button, Checkbox, Icon, Input } from 'ui';
import {
  calculatePriceTotals,
  calculateTotalPrice,
} from 'utils/productTotalPriceHelper';
import useQueryApiClient from 'utils/useQueryApiClient';
import { useAuth } from 'hooks/useAuth';
import { useCoupons } from './useCoupons';
import CouponManager from './coupon-manager';
import { StyledCartSummary } from './style';
import useMarketType from '../../../../utils/useMarketType';
import PublicRoute from 'enums/publicRoutes';
import { useBreakpoint } from 'utils/useBreakpoint';
import { policyLinks } from 'config/config';

interface CartOrderSummaryItemProps {
  id: string;
  label: string;
  value: string;
}

interface CartSummaryProps {
  farmProducts: FarmProduct[];
  setCurrent?: React.Dispatch<React.SetStateAction<CartStep>>;
  form?: FormInstance;
  current?: CartStep | undefined;
  isView?: boolean | undefined;
  userForm?: FormInstance;
  priceTotal: number;
  isBuying?: boolean;
  upperHandleBuyProducts?: (type: string) => void;
}

const CartSummary = ({
  farmProducts,
  setCurrent,
  form,
  current,
  isView,
  userForm,
  priceTotal,
  isBuying,
  upperHandleBuyProducts,
}: CartSummaryProps) => {
  const [downloadingPdfs, setDownloadingPdfs] = useState(false);
  const [showSaveCart, setShowSaveCart] = useState(false);
  const [cartName, setCartName] = useState<string>();
  const [isCartSaved, setIsCartSaved] = useState<boolean>(false);

  const dispatchCart = useCartDispatch();
  const {
    requiresAuth,
    cartId,
    cartName: stateCartName,
    coupons,
  } = useCartState();
  const {
    totalDiscount: couponTotal,
    vatSavings: couponVatSavings,
    discountByFarm,
  } = useCoupons();
  const intl = useIntl();
  let [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isProcessOrderStep = current === CartStep.PROCESS_ORDER;
  const { user } = useAuth();
  const { isEmarket, marketType } = useMarketType();

  const { isSmDown, isSmUp } = useBreakpoint();

  const { handleDownloadClick, pdfLoading } = useFarmOrderHandler({
    onComplete: () => setDownloadingPdfs(false),
  });

  const orderId = searchParams.get('order_id');

  const { appendData } = useQueryApiClient({
    request: {
      url: `api/v2/cart/:id`,
      method: 'PATCH',
    },
    onSuccess: () => {
      setIsCartSaved(true);
      getCart([], { id: cartId });
    },
  });

  const { appendData: getCart } = useQueryApiClient({
    request: {
      url: `api/v2/cart/:id`,
      disableOnMount: true,
    },
    onSuccess: (response) => {
      dispatchCart({
        type: CartActionType.REFETCH_CART_PRODUCTS,
        farmProducts: response.farmProducts,
        priceTotal: response.priceTotal,
        requiresAuth: response.requires_auth,
        cartId: response.cartId,
        cartName: response.cartName,
        marketType: response.marketType,
      });
    },
  });

  const { appendData: deleteCart } = useQueryApiClient({
    request: {
      url: `api/v2/cart/:id`,
      method: 'DELETE',
    },
    onSuccess: () => setIsCartSaved(false),
  });

  const handleError = () => {
    message.error(intl.formatMessage({ id: 'message.payment_failed' }));
    navigate(
      (isEmarket ? PublicRoute.CART_PAGE : PublicRoute.WHOLESALE_CART) +
        '?PROCESS_ORDER=1'
    );
  };

  const summaryTitle =
    current === CartStep.RECEIVE_ORDER
      ? intl.formatMessage({ id: 'cart_summary.order_information' })
      : intl.formatMessage({ id: 'cart_summary.order_summary' });

  useEffect(() => {
    const status = searchParams.get('status');

    if (status) {
      if (status === 'success') {
        setCurrent && setCurrent(CartStep.RECEIVE_ORDER);
      } else if (status === 'failed') {
        handleError();
      }
    }

    return () => {
      if (searchParams.get('status') === 'success' && isView) {
        localStorage.removeItem(getCartStorageKey('farmProducts'));
        localStorage.removeItem(getCartStorageKey('priceTotal'));
        dispatchCart({
          type: CartActionType.CLEAR_CART,
        });
      }
    };
  }, [searchParams, navigate]);

  const priceTotals = calculatePriceTotals(farmProducts);

  const vatSummaryItems: CartOrderSummaryItemProps[] = Object.values(
    priceTotals.groupedVats
  )
    .filter((groupedVat) => groupedVat.totalVat > 0)
    .map((groupedVat) => ({
      id: `cart_summary.vat_${groupedVat.vatRate}`,
      label:
        intl.formatMessage({ id: `cart_summary.PVN` }) +
        ` ${groupedVat.vatRate}%`,
      value: `${groupedVat.totalVat.toFixed(2)} €`,
    }));
  const vatTotal = Math.max(
    priceTotals.totalVat -
      couponVatSavings.reduce(
        (accumulatedSavings, vatSaving) =>
          accumulatedSavings + vatSaving.vatSaved,
        0
      ),
    0
  );

  const cartSummaryPvn: CartOrderSummaryItemProps =
    vatTotal > 0
      ? {
          id: 'cart_summary.PVN',
          label: intl.formatMessage({ id: 'cart_summary.PVN' }),
          value: `${vatTotal.toFixed(2)} €`,
        }
      : ({} as CartOrderSummaryItemProps);

  const deliveryTotal = farmProducts.reduce(
    (total, farm) =>
      farm.selectedDelivery?.price
        ? total + farm.selectedDelivery?.price
        : total,
    0
  );

  const isAllDeliveriesSelected = farmProducts.reduce(
    (total, farm) => (farm.selectedDelivery ? total + 1 : total),
    0
  );

  const handleOrderAgain = () => {
    dispatchCart({
      type: CartActionType.CLEAR_CART,
    });

    const priceTotal = calculateTotalPrice(farmProducts);

    dispatchCart({
      type: CartActionType.REFETCH_CART_PRODUCTS,
      farmProducts,
      priceTotal,
      requiresAuth,
      cartId: undefined,
      marketType: marketType,
    });

    navigate(isEmarket ? PublicRoute.CART_PAGE : PublicRoute.WHOLESALE_CART);
  };

  const getFarmSummaryItems = (farm: FarmProduct) => {
    const totalCouponDiscount = discountByFarm[farm.farm_id] ?? 0;

    const items = [
      {
        id: 'delivery.price',
        label: intl.formatMessage({ id: 'delivery.price' }),
        value: `${farm.farmPriceTotals.totalWithDiscount.toFixed(2)} €`,
      },
      {
        id: 'general.discount',
        label: intl.formatMessage({ id: 'general.discount' }),
        value: `${(
          farm.farmPriceTotals.totalWithoutDiscount -
          farm.farmPriceTotals.totalWithDiscount
        ).toFixed(2)} €`,
      },
    ];

    if (totalCouponDiscount > 0) {
      items.push({
        id: 'general.coupon_discount',
        label: intl.formatMessage({ id: 'cart_summary.coupon.total_discount' }),
        value: `${totalCouponDiscount.toFixed(2)} €`,
      });
    }

    if (isEmarket) {
      items.push({
        id: 'product.delivery',
        label: intl.formatMessage({ id: 'product.delivery' }),
        value:
          farm.selectedDelivery?.price !== undefined &&
          farm.selectedDelivery?.price !== null &&
          farm.selectedDelivery.price !== 0
            ? `${farm.selectedDelivery.price.toFixed(2)} €`
            : intl.formatMessage({ id: 'delivery.free_delivery' }),
      });
    }

    return items;
  };

  function PoliticsCheckbox() {
    const intl = useIntl();

    return (
      <Checkbox
        name="agree_to_politics"
        validations={'required'}
        label={
          <FormattedMessage
            id="cart_summary.agree_to_politics"
            values={{
              politicsLink: (
                <a
                  href={policyLinks.privacyPolicy}
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {intl.formatMessage({ id: 'cart_summary.politics' })}
                </a>
              ),
            }}
          />
        }
      />
    );
  }

  function TermsCheckbox() {
    const intl = useIntl();

    return (
      <Checkbox
        name="agree_to_terms"
        validations={'required'}
        label={
          <FormattedMessage
            id="cart_summary.agree_to_terms"
            values={{
              termsLink: (
                <a
                  href={PublicRoute.TERMS_OF_USE}
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {intl.formatMessage({ id: 'cart_summary.terms' })}
                </a>
              ),
            }}
          />
        }
      />
    );
  }

  const summaryItems: CartOrderSummaryItemProps[] = [
    {
      id: 'cart_summary.price_no_discount',
      label: intl.formatMessage({ id: 'cart_summary.price_no_discount' }),
      value: `${priceTotals.totalWithoutDiscount.toFixed(2)} €`,
    },
    {
      id: 'general.discount',
      label: intl.formatMessage({ id: 'general.discount' }),
      value: `${(
        priceTotals.totalWithoutDiscount - priceTotals.totalWithDiscount
      ).toFixed(2)} €`,
    },
    ...(couponTotal > 0
      ? [
          {
            id: 'cart_summary.coupon.coupon_total',
            label: intl.formatMessage({
              id: 'cart_summary.coupon.coupon_total',
            }),
            value: `${couponTotal.toFixed(2)} €`,
          },
        ]
      : []),
    {
      id: 'cart_summary.sum_without_PVN',
      label: intl.formatMessage({ id: 'cart_summary.sum_without_PVN' }),
      value: `${Math.max(
        priceTotals.totalWithDiscount -
          couponTotal -
          (priceTotals.totalVat -
            couponVatSavings.reduce(
              (accumulatedSavings, vatSaving) =>
                accumulatedSavings + vatSaving.vatSaved,
              0
            )),
        0
      ).toFixed(2)} €`,
    },
    ...(isEmarket || vatTotal > 0 ? [cartSummaryPvn] : []),
    ...(isEmarket || vatTotal > 0
      ? vatSummaryItems.map((item) => {
          const rate = Number(item.id.replace('cart_summary.vat_', ''));
          const saved =
            couponVatSavings.find((g) => g.vatRate === rate)?.vatSaved ?? 0;
          const orig = priceTotals.groupedVats.find(
            (g) => g.vatRate === rate
          )!.totalVat;
          return {
            ...item,
            value: `${Math.max(orig - saved, 0).toFixed(2)} €`,
          };
        })
      : []),
    ...(isEmarket
      ? [
          {
            id: 'cart_summary.delivery_price',
            label: intl.formatMessage({ id: 'cart_summary.delivery_price' }),
            value: `${deliveryTotal.toFixed(2)} €`,
          },
        ]
      : []),
  ];

  const handleBuyProducts = async () => {
    if (CartStep.PROCESS_ORDER === current && isEmarket) {
      upperHandleBuyProducts?.('PAY');
      return;
    }

    if (!setCurrent) return;

    if (
      current !== undefined &&
      current >= CartStep.PROCESS_ORDER &&
      userForm
    ) {
      return;
    }

    if (!form && current !== undefined && current <= CartStep.PROCESS_ORDER) {
      setCurrent((current + 1) as CartStep);
      return;
    }

    if (current !== undefined && current <= CartStep.PROCESS_ORDER)
      setCurrent((current + 1) as CartStep);
  };

  return (
    <StyledCartSummary>
      <Form form={form} layout="vertical" onFinish={handleBuyProducts}>
        <div className={'cart-summary'}>
          {!isView && (
            <div className={'cart-summary-title'}>{summaryTitle}</div>
          )}
          <div className={'cart-order-summary-wrapper'}>
            {!isView &&
              !isEmarket &&
              (!stateCartName && !isCartSaved ? (
                <div className="cart-order-summary-save-cart">
                  <Checkbox
                    label={intl.formatMessage({ id: 'cart_summary.save_cart' })}
                    onChange={(e) => setShowSaveCart(e.target.checked)}
                    checked={showSaveCart}
                  />
                  {showSaveCart && (
                    <div className="cart-order-summary-save-cart-input">
                      <Input
                        noStyle
                        onChange={(e) => setCartName(e.target.value)}
                      />
                      <Button
                        label={intl.formatMessage({ id: 'general.save' })}
                        onClick={() => {
                          appendData(
                            {
                              name: cartName,
                              farmProducts,
                              priceTotal,
                              coupons,
                              marketType,
                              cartId,
                            },
                            { id: cartId }
                          );
                        }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <span className="cart-name">{stateCartName}</span>
                  </div>
                  <Button
                    label={intl.formatMessage({
                      id: 'cart_summary.delete_cart',
                    })}
                    type="text"
                    onClick={() => {
                      deleteCart([], { id: cartId });
                      dispatchCart({
                        type: CartActionType.CLEAR_CART,
                      });
                    }}
                  />
                </>
              ))}
            {isView ? (
              <div className={'cart-order-summary-collection'}>
                <CartOrderSummary
                  key={Math.random().toString(36)}
                  items={[
                    {
                      id: Math.random().toString(36),
                      label: intl.formatMessage({
                        id: 'cart_summary.order_number',
                      }),
                      value: '#' + searchParams.get('order_id') || '',
                    },
                    {
                      id: Math.random().toString(36),
                      label: intl.formatMessage({
                        id: 'cart_summary.order_date',
                      }),
                      value: searchParams.get('date') || '',
                    },
                  ]}
                  title={intl.formatMessage({ id: 'cart_summary.order' })}
                />
              </div>
            ) : (
              <div className={'cart-order-summary-collection'}>
                {farmProducts.map((farm) => (
                  <CartOrderSummary
                    key={farm.farm_id}
                    items={getFarmSummaryItems(farm)}
                    title={farm.farm_name}
                  />
                ))}
              </div>
            )}
            <CartOrderSummary
              className={isSmDown ? 'mobile-total' : 'total'}
              items={summaryItems}
            />
            {!isView && isSmDown && (
              <div className={'cart-summary-final-price-title-wrap'}>
                <div className={'cart-summary-final-price-title'}>
                  {intl.formatMessage({ id: 'cart_summary.total' })}
                </div>
                <div className={'cart-summary-price'}>
                  {Math.max(priceTotal, 0).toFixed(2)} €
                </div>
              </div>
            )}
            {!isView && <CouponManager />}
            <div className={'cart-summary-submit-wrapper'}>
              <div className={'cart-summary-final-price'}>
                {(isProcessOrderStep || (!isEmarket && !isView)) && (
                  <div className={'rules'}>
                    <div>
                      {isEmarket && (
                        <Checkbox
                          name={'subscribe_to_news'}
                          label={intl.formatMessage({
                            id: 'register.accept_terms',
                          })}
                        />
                      )}
                      <TermsCheckbox />
                      <PoliticsCheckbox />
                    </div>
                  </div>
                )}
                {isEmarket && (isView || isSmUp) && (
                  <div className={'cart-summary-final-price-title-wrap view'}>
                    <div className={'cart-summary-final-price-title'}>
                      {intl.formatMessage({ id: 'cart_summary.total' })}
                    </div>
                    <div className={'cart-summary-price'}>
                      {Math.max(priceTotal, 0).toFixed(2)} €
                    </div>
                  </div>
                )}
              </div>
              {requiresAuth && (
                <div className={'requires-auth'}>
                  {intl.formatMessage({ id: 'message.requiresAuth' })}
                </div>
              )}
              {isView && !searchParams.get('status') && (
                <Button
                  type="primary"
                  htmlType="submit"
                  className={'cart-summary-submit-btn-view order-again'}
                  label={intl.formatMessage({
                    id: 'button.order_again',
                  })}
                  onClick={() => handleOrderAgain()}
                />
              )}
              {!isView
                ? isEmarket && (
                    <Button
                      disabled={
                        isAllDeliveriesSelected !== farmProducts.length ||
                        farmProducts.length <= 0
                      }
                      type="primary"
                      htmlType="submit"
                      className={'cart-summary-submit-btn'}
                      label={intl.formatMessage({ id: 'cart_summary.buy' })}
                      loading={isBuying}
                    />
                  )
                : user?.authenticated &&
                  orderId &&
                  isEmarket &&
                  (() => {
                    const isWholesale = (fp: FarmProduct) =>
                      [
                        'order',
                        'purchase',
                        'reservation',
                        'dynamic_purchase',
                      ].includes(fp.transaction_type?.toLowerCase() ?? '');
                    const isNew = (fp: FarmProduct) =>
                      fp.transaction_status?.toLowerCase() === 'new';
                    const canDownload = (fp: FarmProduct) =>
                      !(isWholesale(fp) && isNew(fp));
                    const hasTransactionData = farmProducts.some(
                      (fp: FarmProduct) => fp.transaction_status !== undefined
                    );
                    const downloadableFarms = hasTransactionData
                      ? farmProducts.filter(canDownload)
                      : farmProducts;
                    const canShowDownload = hasTransactionData
                      ? downloadableFarms.length > 0
                      : true;

                    return (
                      canShowDownload && (
                        <>
                          <Button
                            type="primary"
                            htmlType="submit"
                            className={'cart-summary-submit-btn-view'}
                            label={intl.formatMessage({
                              id: 'cart_summary.download_invoice',
                            })}
                            loading={downloadingPdfs || pdfLoading}
                            onClick={() => {
                              setDownloadingPdfs(true);
                              const orders = downloadableFarms.map(
                                (fp: FarmProduct) => ({
                                  order_id: orderId || '',
                                  farm_id: fp.farm_id,
                                })
                              );
                              handleDownloadClick(orders);
                            }}
                          />
                          <p>
                            {intl.formatMessage({
                              id: 'process_purchase.saved_purchase_message',
                            })}
                          </p>
                        </>
                      )
                    );
                  })()}
            </div>
          </div>
        </div>

        {isAllDeliveriesSelected === farmProducts.length &&
          isView === false && (
            <div className="total-summary">
              <p className="total-summary-text">
                <span>
                  {intl.formatMessage({ id: 'cart_summary.amount_be_paid' })}
                </span>
                <b>{priceTotal.toFixed(2)} €</b>
              </p>
              <Button
                icon={<Icon name={'arrow-right'} />}
                type="primary"
                htmlType="submit"
                iconPosition="end"
                label={intl.formatMessage({ id: 'cart_summary.pay' })}
              />
            </div>
          )}
      </Form>
    </StyledCartSummary>
  );
};

export default CartSummary;

const CartOrderSummary = ({
  items,
  title,
  className = '',
}: {
  items?: CartOrderSummaryItemProps[];
  title?: string;
  className?: string;
}) => (
  <div className={`cart-farm-order-summary ${className}`}>
    {title && <div className="farm-title">{title}</div>}
    <div className={'farm-summary-wrapper'}>
      {items &&
        items.map((item) => (
          <div className={'cart-summary-container'} key={item.id}>
            <p className={'cart-summary-item'}>{item.label}</p>
            <p className={'cart-summary-item price'}>{item.value}</p>
          </div>
        ))}
    </div>
  </div>
);
